// Width at/above which the customer app renders its desktop layout (the desktop
// nav appears at min-width 993px; 992 is the lg/xl breakpoint boundary).
export const DESKTOP_LAYOUT_MIN_WIDTH = 992;

// sessionStorage key for "user dismissed the banner this session".
export const DISMISS_KEY = 'desktopSiteBannerDismissed';

// Pure, storage-injected dismissal helpers so the persist/pre-dismissed behavior
// is unit-testable without a DOM. Both degrade safely if storage throws (Safari
// private mode) or is unavailable.
export function readDismissed(storage) {
  try {
    return Boolean(storage) && storage.getItem(DISMISS_KEY) === '1';
  } catch (e) {
    return false;
  }
}

export function markDismissed(storage) {
  try {
    if (storage) storage.setItem(DISMISS_KEY, '1');
  } catch (e) {
    // private mode / disabled storage — nothing to persist, caller still hides.
  }
}

// Decide whether to show the "you're in Desktop Site mode" banner.
//
// We deliberately do NOT look at the user-agent: when Android Chrome / iOS Safari
// have "Desktop site" enabled they ALSO spoof the UA to a desktop string, so the
// UA is useless here. The reliable signature of a phone/tablet showing the
// desktop-width layout is: a touch-capable device whose viewport has fallen into
// the desktop breakpoint. A real desktop reports no touch points; a normal phone
// reports a mobile-width viewport — neither triggers.
//
// Known tradeoff: a touchscreen laptop or a large tablet in landscape can be a
// false positive. The banner is dismissible and non-blocking, so we bias toward
// catching the stranded phone user over avoiding the occasional benign nudge.
export default function shouldShowDesktopSiteBanner({
  maxTouchPoints = 0,
  hasTouchStart = false,
  innerWidth = 0,
  dismissed = false,
} = {}) {
  if (dismissed) return false;

  const isTouchDevice = maxTouchPoints > 0 || hasTouchStart;
  const isDesktopWidth = innerWidth >= DESKTOP_LAYOUT_MIN_WIDTH;

  return isTouchDevice && isDesktopWidth;
}
